import React, { useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";

import List from "@mui/material/List";
import Paper from "@mui/material/Paper";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";

import { baseURL } from "../../api";
import { Global_Data } from "../../globalData/GlobalData";

export default function TransferListMain({ data }) {
  const { token, textValue, setTextValue } = Global_Data();

  const [checked, setChecked] = React.useState([]);
  const [left, setLeft] = React.useState([]);
  const [right, setRight] = React.useState([]);
  const [loadingChoices, setLoadingChoices] = React.useState(false);
  const [loadingChosen, setLoadingChosen] = React.useState(false);
  const [leftSearch, setLeftSearch] = React.useState("");
  const [rightSearch, setRightSearch] = React.useState("");

  const previousDataAwareValues = useRef(null);
  const hasMountedCascade = useRef(false);
  const rightRef = useRef([]);
  const hasLoadedExistingChosen = useRef(false);

  const component = data?.component || {};
  const content = component?.content || {};
  const componentName = component?.sName;
  const transferListProps = component?.sProps || {};

  const choiceConfig = content?.sChoices || {};
  const chosenConfig = content?.sChoosen || {};
  const isCascade = content?.bCascade === true;

  useEffect(() => {
    rightRef.current = right;
  }, [right]);

  function getDataAwareFields(sDataAware = "") {
    return (
      sDataAware
        .match(/{([^}]+)}/g)
        ?.map(item => item.replace(/[{}]/g, "").trim())
        .filter(Boolean) || []
    );
  }

  function hasRequiredTemplateValues(template = "", formValues = {}) {
    const fields =
      template
        .match(/{([^}]+)}/g)
        ?.map(item => item.replace(/[{}]/g, "").trim())
        .filter(Boolean) || [];

    return fields.every(field => {
      const value = formValues?.[field];
      return value !== undefined && value !== null && String(value).trim() !== "";
    });
  }

  function generateDynamicUri(template = "", formValues = {}) {
    return template.replace(/{(\w+)}/g, (_, key) => {
      const value = formValues?.[key];
      return value !== undefined && value !== null ? value : "";
    });
  }

  function getValueByPath(obj, path = "") {
    if (!obj || !path) return undefined;

    if (Object.prototype.hasOwnProperty.call(obj, path)) {
      return obj[path];
    }

    const normalizedPath = path.replace(/\[(\d+)\]/g, ".$1");

    return normalizedPath.split(".").reduce((acc, key) => {
      if (acc === undefined || acc === null) return undefined;
      return acc[key];
    }, obj);
  }

  function getValueFromBinding(
    rawItem,
    bindingKey,
    binding,
    currentTextValue = {},
    context = {}
  ) {
    const source = binding?.source;

    if (
      typeof source === "string" &&
      source.startsWith("{") &&
      source.endsWith("}")
    ) {
      const formField = source.replace(/[{}]/g, "").trim();
      return currentTextValue?.[formField];
    }

    if (typeof source === "string" && source.trim() !== "") {
      const sourceParts = source.split(".");
      const arrayPartIndex = sourceParts.findIndex(part => part.endsWith("[]"));

      if (arrayPartIndex !== -1) {
        const parentKey = sourceParts[arrayPartIndex].replace("[]", "");
        const parentItem = context[parentKey];
        const remainingPath = sourceParts.slice(arrayPartIndex + 1).join(".");

        if (parentItem && remainingPath) {
          return getValueByPath(parentItem, remainingPath);
        }
      }

      return getValueByPath(rawItem, source);
    }

    return getValueByPath(rawItem, bindingKey);
  }

  function extractByRoot(responseData, root = "") {
    if (!root) {
      return Array.isArray(responseData)
        ? responseData.map(item => ({ item, context: {} }))
        : [];
    }

    const parts = root.split(".");
    let currentItems = [{ item: responseData, context: {} }];

    parts.forEach(part => {
      const isArrayPart = part.endsWith("[]");
      const cleanPart = part.replace("[]", "");

      currentItems = currentItems.flatMap(entry => {
        const value = entry.item?.[cleanPart];

        if (isArrayPart) {
          if (!Array.isArray(value)) return [];

          return value.map(childItem => ({
            item: childItem,
            context: {
              ...entry.context,
              [cleanPart]: childItem
            }
          }));
        }

        if (value === undefined || value === null) return [];

        return [
          {
            item: value,
            context: entry.context
          }
        ];
      });
    });

    return currentItems;
  }

  function applyDisplayMapping(responseData, displayMapping, currentTextValue = {}) {
    const extractedItems = extractByRoot(responseData, displayMapping?.root);
    const bindings = displayMapping?.bindings || {};

    return extractedItems.map(({ item: rawItem, context }) => {
      const mappedItem = {
        __raw: rawItem,
        __context: context
      };

      Object.keys(bindings).forEach(bindingKey => {
        mappedItem[bindingKey] = getValueFromBinding(
          rawItem,
          bindingKey,
          bindings[bindingKey],
          currentTextValue,
          context
        );
      });

      return mappedItem;
    });
  }

  function escapeHtml(value) {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function renderHtmlTemplate(template = "", item = {}) {
    let html = template;

    html = html.replace(
      /<span\s+data-bind-text="([^"]+)"\s*><\/span>/g,
      (_, fieldName) => {
        return `<span>${escapeHtml(getValueByPath(item, fieldName))}</span>`;
      }
    );

    html = html.replace(
      /<img([^>]*)data-bind-src="([^"]+)"([^>]*)\/?>/g,
      (_, beforeAttrs, fieldName, afterAttrs) => {
        const imageValue = getValueByPath(item, fieldName);

        if (
          imageValue === undefined ||
          imageValue === null ||
          String(imageValue).trim() === ""
        ) {
          return "";
        }

        return `<img${beforeAttrs}src="${escapeHtml(imageValue)}"${afterAttrs} />`;
      }
    );

    html = html.replace(/\sdata-repeat="[^"]+"/g, "");

    return html;
  }

  function parseValueFields(valueFields = "") {
    return valueFields
      .split(",")
      .map(field => field.trim())
      .filter(Boolean)
      .map(field => {
        const isFormField = field.startsWith("{") && field.endsWith("}");
        const cleanField = field.replace(/[{}]/g, "").trim();

        return {
          field: cleanField,
          source: isFormField ? "form" : "item"
        };
      });
  }

  function mapChoiceToChosenItem(choiceItem) {
    const transferMapping =
      choiceConfig?.sTransferFields?.choosenMapping || {};

    const chosenItem = {};

    Object.keys(transferMapping).forEach(targetField => {
      const sourceField = transferMapping[targetField];
      chosenItem[targetField] = getValueByPath(choiceItem, sourceField);
    });

    return chosenItem;
  }

  function mapChosenToChoiceItem(chosenItem) {
    const transferMapping =
      choiceConfig?.sTransferFields?.choosenMapping || {};

    const choiceItem = {};

    Object.keys(transferMapping).forEach(targetField => {
      const sourceField = transferMapping[targetField];
      choiceItem[sourceField] = getValueByPath(chosenItem, targetField);
    });

    return choiceItem;
  }

  function buildPayload(rightItems, currentTextValue = {}) {
    const valueFields = parseValueFields(chosenConfig?.sValueFields);

    return rightItems.map(item => {
      const payloadItem = {};

      valueFields.forEach(({ field, source }) => {
        if (source === "form") {
          payloadItem[field] = currentTextValue?.[field];
        } else {
          payloadItem[field] = getValueByPath(item, field);
        }
      });

      return payloadItem;
    });
  }

  function updatePayload(nextRight) {
    if (!componentName) return;

    setTextValue(prev => ({
      ...prev,
      [componentName]: JSON.stringify(buildPayload(nextRight, prev))
    }));
  }

  function getItemKey(item, side) {
    if (item?.__transferKey) {
      return item.__transferKey;
    }

    const keyObject = {};

    if (side === "right") {
      Object.keys(chosenConfig?.sDisplayMapping?.bindings || {})
        .sort()
        .forEach(field => {
          keyObject[field] = getValueByPath(item, field) ?? "";
        });
    }

    if (side === "left") {
      const transferMapping =
        choiceConfig?.sTransferFields?.choosenMapping || {};

      Object.keys(transferMapping)
        .sort()
        .forEach(targetField => {
          const sourceField = transferMapping[targetField];
          keyObject[targetField] = getValueByPath(item, sourceField) ?? "";
        });
    }

    const hasValue = Object.values(keyObject).some(
      value => String(value ?? "").trim() !== ""
    );

    if (hasValue) {
      return JSON.stringify(keyObject);
    }

    return JSON.stringify(item);
  }

  function attachTransferKeys(items, side) {
    return items.map(item => ({
      ...item,
      __transferKey: getItemKey(item, side)
    }));
  }

  function parseExistingChosenValue(value) {
    if (!value || typeof value !== "string") return [];

    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

function getChosenComparableKey(item) {
  const valueFields = parseValueFields(chosenConfig?.sValueFields)
    .filter(valueField => valueField.source === "item")
    .map(valueField => valueField.field);

  const keyObject = {};

  valueFields.forEach(field => {
    keyObject[field] = getValueByPath(item, field) ?? "";
  });

  return JSON.stringify(keyObject);
}

function removeAlreadyChosenFromLeft(choiceItems, chosenItems) {
  const chosenKeys = new Set(
    chosenItems
      .map(item => getChosenComparableKey(item))
      .filter(key => key && key !== "{}")
  );

  if (chosenKeys.size === 0) {
    return choiceItems;
  }

  return choiceItems.filter(choiceItem => {
    const mappedChoice = mapChoiceToChosenItem(choiceItem);
    const mappedChoiceKey = getChosenComparableKey(mappedChoice);

    if (!mappedChoiceKey || mappedChoiceKey === "{}") {
      return true;
    }

    return !chosenKeys.has(mappedChoiceKey);
  });
}

  function getDisplaySearchText(item, displayMapping) {
    const bindings = displayMapping?.bindings || {};

    return Object.keys(bindings)
      .map(field => getValueByPath(item, field))
      .filter(value => value !== undefined && value !== null)
      .join(" ")
      .toLowerCase();
  }

  function filterItemsBySearch(items, searchText, displayMapping) {
    const keyword = String(searchText || "").trim().toLowerCase();

    if (!keyword) return items;

    return items.filter(item =>
      getDisplaySearchText(item, displayMapping).includes(keyword)
    );
  }

  const dataAwareFields = useMemo(() => {
    return getDataAwareFields(content?.sDataAware);
  }, [content?.sDataAware]);

  const dataAwareValuesKey = useMemo(() => {
    const values = {};

    dataAwareFields.forEach(field => {
      values[field] = textValue?.[field];
    });

    return JSON.stringify(values);
  }, [dataAwareFields, textValue]);

  useEffect(() => {
    if (hasLoadedExistingChosen.current) return;

    const existingValue = textValue?.[componentName];
    const parsedChosen = parseExistingChosenValue(existingValue);

    if (parsedChosen.length === 0) return;

    const keyedChosen = attachTransferKeys(parsedChosen, "right");

    hasLoadedExistingChosen.current = true;
    rightRef.current = keyedChosen;
    setRight(keyedChosen);
    setRightSearch("");

    setLeft(prevLeft => removeAlreadyChosenFromLeft(prevLeft, keyedChosen));
  }, [componentName, textValue?.[componentName]]);

  const filteredLeft = filterItemsBySearch(
    left,
    leftSearch,
    choiceConfig?.sDisplayMapping
  );

  const filteredRight = filterItemsBySearch(
    right,
    rightSearch,
    chosenConfig?.sDisplayMapping
  );

  const fetchChoices = useCallback(
    async currentTextValue => {
      try {
        const choiceSource = choiceConfig?.sChoiceSource;

        if (!choiceSource) return;

        if (!hasRequiredTemplateValues(choiceSource, currentTextValue)) {
          setLeft([]);
          setChecked([]);
          setLeftSearch("");
          return;
        }

        const dynamicUri = generateDynamicUri(choiceSource, currentTextValue);

        setLoadingChoices(true);

        const result = await axios.get(`${baseURL}${dynamicUri}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const mappedChoices = applyDisplayMapping(
          result.data,
          choiceConfig?.sDisplayMapping,
          currentTextValue
        );

        const keyedChoices = attachTransferKeys(mappedChoices, "left");

        const availableChoices = removeAlreadyChosenFromLeft(
          keyedChoices,
          rightRef.current || []
        );

        setLeft(availableChoices);
        setChecked([]);
        setLeftSearch("");
      } catch (error) {
        console.log("Error loading TRANSFERLIST choices:", error);
      } finally {
        setLoadingChoices(false);
      }
    },
    [choiceConfig?.sChoiceSource, choiceConfig?.sDisplayMapping, token]
  );

  const fetchChosen = useCallback(
    async currentTextValue => {
      try {
        const chosenSource = chosenConfig?.sChoosen;

        if (!chosenSource) return;

        if (!hasRequiredTemplateValues(chosenSource, currentTextValue)) {
          setRight([]);
          rightRef.current = [];
          updatePayload([]);
          setRightSearch("");
          return;
        }

        const dynamicUri = generateDynamicUri(chosenSource, currentTextValue);

        setLoadingChosen(true);

        const result = await axios.get(`${baseURL}${dynamicUri}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const mappedChosen = applyDisplayMapping(
          result.data,
          chosenConfig?.sDisplayMapping,
          currentTextValue
        );

        const keyedChosen = attachTransferKeys(mappedChosen, "right");

        setRight(keyedChosen);
        rightRef.current = keyedChosen;
        updatePayload(keyedChosen);
        setRightSearch("");
      } catch (error) {
        console.log("Error loading TRANSFERLIST chosen items:", error);
      } finally {
        setLoadingChosen(false);
      }
    },
    [chosenConfig?.sChoosen, chosenConfig?.sDisplayMapping, token]
  );

  useEffect(() => {
    fetchChosen(textValue);
  }, []);

  useEffect(() => {
    if (!isCascade) {
      fetchChoices(textValue);
    }
  }, [isCascade]);

  useEffect(() => {
    if (!isCascade) return;

    if (!hasMountedCascade.current) {
      hasMountedCascade.current = true;
      previousDataAwareValues.current = dataAwareValuesKey;
      return;
    }

    if (previousDataAwareValues.current === dataAwareValuesKey) {
      return;
    }

    previousDataAwareValues.current = dataAwareValuesKey;
    fetchChoices(textValue);
  }, [isCascade, dataAwareValuesKey]);

  const leftChecked = filteredLeft.filter(item =>
    checked.includes(getItemKey(item, "left"))
  );

  const rightChecked = filteredRight.filter(item =>
    checked.includes(getItemKey(item, "right"))
  );

  function handleToggle(item, side) {
    return () => {
      const key = getItemKey(item, side);

      setChecked(prev => {
        if (prev.includes(key)) {
          return prev.filter(itemKey => itemKey !== key);
        }

        return [...prev, key];
      });
    };
  }

  function handleCheckedRight() {
    const transferredItems = attachTransferKeys(
      leftChecked.map(mapChoiceToChosenItem),
      "right"
    );

    const nextRight = [...right, ...transferredItems];
    const movedLeftKeys = leftChecked.map(item => getItemKey(item, "left"));

    setRight(nextRight);
    rightRef.current = nextRight;

    setLeft(prev =>
      prev.filter(item => !movedLeftKeys.includes(getItemKey(item, "left")))
    );

    setChecked(prev => prev.filter(key => !movedLeftKeys.includes(key)));
    updatePayload(nextRight);
  }

  function handleCheckedLeft() {
    const movedRightKeys = rightChecked.map(item => getItemKey(item, "right"));

    const returnedItems = attachTransferKeys(
      rightChecked.map(mapChosenToChoiceItem),
      "left"
    );

    const nextLeft = [...left, ...returnedItems];

    const nextRight = right.filter(
      item => !movedRightKeys.includes(getItemKey(item, "right"))
    );

    setLeft(nextLeft);
    setRight(nextRight);
    rightRef.current = nextRight;

    setChecked(prev => prev.filter(key => !movedRightKeys.includes(key)));
    updatePayload(nextRight);
  }

  function handleAllRight() {
    const transferredItems = attachTransferKeys(
      filteredLeft.map(mapChoiceToChosenItem),
      "right"
    );

    const nextRight = [...right, ...transferredItems];
    const movedLeftKeys = filteredLeft.map(item => getItemKey(item, "left"));

    setRight(nextRight);
    rightRef.current = nextRight;

    setLeft(prev =>
      prev.filter(item => !movedLeftKeys.includes(getItemKey(item, "left")))
    );

    setChecked([]);
    updatePayload(nextRight);
  }

  function handleAllLeft() {
    const movedRightKeys = filteredRight.map(item => getItemKey(item, "right"));

    const returnedItems = attachTransferKeys(
      filteredRight.map(mapChosenToChoiceItem),
      "left"
    );

    const nextLeft = [...left, ...returnedItems];

    const nextRight = right.filter(
      item => !movedRightKeys.includes(getItemKey(item, "right"))
    );

    setLeft(nextLeft);
    setRight(nextRight);
    rightRef.current = nextRight;

    setChecked([]);
    updatePayload(nextRight);
  }

  function buildTransferListContainerProps() {
    const { sx, ...otherProps } = transferListProps;

    return {
      ...otherProps,
      sx: {
        width: "100%",
        height: 600,
        p: 2,
        boxSizing: "border-box",
        overflow: "hidden",
        ...sx
      }
    };
  }

  function renderCustomList({
    items,
    side,
    displayFormat,
    displayMapping,
    loading,
    searchValue,
    onSearchChange,
    placeholder
  }) {
    const filteredItems = filterItemsBySearch(
      items,
      searchValue,
      displayMapping
    );

    const listPaperProps = {
      variant: "outlined",
      sx: {
        width: "100%",
        height: "100%",
        overflow: "hidden",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column"
      }
    };

    return (
      <Paper {...listPaperProps}>
        <Box sx={{ p: 1 }}>
          <TextField
            fullWidth
            size="small"
            value={searchValue}
            onChange={event => onSearchChange(event.target.value)}
            placeholder={placeholder}
            disabled={loading}
          />
        </Box>

        {loading ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <List
            dense
            component="div"
            role="list"
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              minHeight: 0
            }}
          >
            {filteredItems.map(item => {
              const key = getItemKey(item, side);
              const labelId = `transfer-list-item-${key}-label`;

              return (
                <ListItem
                  key={key}
                  role="listitem"
                  button
                  onClick={handleToggle(item, side)}
                  sx={{ alignItems: "flex-start" }}
                >
                  <ListItemIcon sx={{ minWidth: 36, mt: 1 }}>
                    <Checkbox
                      checked={checked.includes(key)}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{
                        "aria-labelledby": labelId
                      }}
                    />
                  </ListItemIcon>

                  <Box
                    id={labelId}
                    sx={{
                      width: "100%",
                      minWidth: 0,
                      overflow: "hidden",
                      "& img": {
                        maxWidth: 80,
                        maxHeight: 80,
                        objectFit: "contain"
                      }
                    }}
                    dangerouslySetInnerHTML={{
                      __html: renderHtmlTemplate(displayFormat, item)
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Paper>
    );
  }

  return (
    <Paper {...buildTransferListContainerProps()}>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) 80px minmax(0, 1fr)",
          columnGap: 2,
          boxSizing: "border-box",
          overflow: "hidden",
          minHeight: 0
        }}
      >
        <Box
          sx={{
            height: "100%",
            minWidth: 0,
            minHeight: 0,
            overflow: "hidden"
          }}
        >
          {renderCustomList({
            items: left,
            side: "left",
            displayFormat: choiceConfig?.sDisplayFormat,
            displayMapping: choiceConfig?.sDisplayMapping,
            loading: loadingChoices,
            searchValue: leftSearch,
            onSearchChange: setLeftSearch,
            placeholder: "Search choices..."
          })}
        </Box>

        <Box
          sx={{
            height: "100%",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleAllRight}
            disabled={filteredLeft.length === 0 || loadingChoices}
          >
            ≫
          </Button>

          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0 || loadingChoices}
          >
            &gt;
          </Button>

          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedLeft}
            disabled={rightChecked.length === 0 || loadingChosen}
          >
            &lt;
          </Button>

          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleAllLeft}
            disabled={filteredRight.length === 0 || loadingChosen}
          >
            ≪
          </Button>
        </Box>

        <Box
          sx={{
            height: "100%",
            minWidth: 0,
            minHeight: 0,
            overflow: "hidden"
          }}
        >
          {renderCustomList({
            items: right,
            side: "right",
            displayFormat: chosenConfig?.sDisplayFormat,
            displayMapping: chosenConfig?.sDisplayMapping,
            loading: loadingChosen,
            searchValue: rightSearch,
            onSearchChange: setRightSearch,
            placeholder: "Search chosen..."
          })}
        </Box>
      </Box>
    </Paper>
  );
}